// externals
import type { ComponentType, FocusEvent } from 'react';
import React, { forwardRef } from 'react';

// libraries
import { deferredSelect, useSelectOnFocusEnabledStrict } from './selectOnFocus';
import type { SelectOnFocusSelector } from './selectOnFocus';

type SelectableFocusEvent = FocusEvent<HTMLInputElement | HTMLTextAreaElement>;
type WithOptionalFocus = { onFocus?: (e: SelectableFocusEvent) => void };

/**
 * Wraps an Interstate input component with select-all-on-focus (so a mouse click
 * highlights the value the same way Tab does). The select is deferred one frame
 * so it survives the click's trailing `mouseup` event. Ref is forwarded so the
 * result is a true drop-in for the wrapped component.
 */
export function withSelectOnFocus<P extends WithOptionalFocus, RefType = HTMLInputElement>(
    Component: ComponentType<P>,
    useEnabled: (selector?: SelectOnFocusSelector) => boolean = useSelectOnFocusEnabledStrict
) {
    type Props = P & { selectOnFocusSelector?: SelectOnFocusSelector };

    const Wrapped = forwardRef<RefType, Props>(({ onFocus, selectOnFocusSelector, ...props }, ref) => {
        const isAutoSelectEnabled = useEnabled(selectOnFocusSelector);

        const handleFocus = (e: SelectableFocusEvent) => {
            onFocus?.(e);
            if (isAutoSelectEnabled) deferredSelect(e.currentTarget);
        };

        return <Component {...(props as P)} ref={ref} onFocus={handleFocus} />;
    });

    return Wrapped;
}
